import { RefObject, useCallback, useState } from 'react';
import { SelectionSyncSide } from '@physna/core/domain/viewer/selection-sync-messaging';
import { useSelectionSync } from '@/hooks/assetViewer/useSelectionSync';

export type SelectionPathsBySide = Record<SelectionSyncSide, string[]>;

const NO_SELECTION: SelectionPathsBySide = { asset1: [], asset2: [] };

type CompareSelection = { comparedAssets: string; paths: SelectionPathsBySide };

export function useCompareSelection({
  iframeReference,
  asset1Id,
  asset2Id,
}: {
  iframeReference: RefObject<HTMLIFrameElement | null>;
  asset1Id?: string;
  asset2Id?: string;
}) {
  const comparedAssets = `${asset1Id}|${asset2Id}`;
  const [selection, setSelection] = useState<CompareSelection>({ comparedAssets, paths: NO_SELECTION });

  // Coming back to an earlier pair must not restore highlights the reloaded viewers no longer hold.
  const isForOtherAssets = selection.comparedAssets !== comparedAssets;
  if (isForOtherAssets) setSelection({ comparedAssets, paths: NO_SELECTION });
  const selectionPaths = isForOtherAssets ? NO_SELECTION : selection.paths;

  const setSelectionPath = useCallback(
    (side: SelectionSyncSide, selectionPath: string[]) => {
      setSelection(current => {
        const paths = current.comparedAssets === comparedAssets ? current.paths : NO_SELECTION;
        return { comparedAssets, paths: { ...paths, [side]: selectionPath } };
      });
    },
    [comparedAssets]
  );

  const handleViewerSelect = useCallback(
    (assetIds: string[], selectionPath?: string[], side?: SelectionSyncSide) => {
      if (!side || assetIds.length === 0) return;
      setSelectionPath(side, selectionPath ?? []);
    },
    [setSelectionPath]
  );

  const handleViewerClear = useCallback(
    (side?: SelectionSyncSide) => {
      if (side) setSelectionPath(side, []);
      else setSelection({ comparedAssets, paths: NO_SELECTION });
    },
    [comparedAssets, setSelectionPath]
  );

  const { sendSelectionToViewer, sendClearToViewer } = useSelectionSync({
    iframeRef: iframeReference,
    onViewerSelect: handleViewerSelect,
    onViewerClear: handleViewerClear,
  });

  const selectFromTree = useCallback(
    (side: SelectionSyncSide, assetId: string, selectionPath?: string[]) => {
      setSelectionPath(side, selectionPath ?? []);
      if (assetId) sendSelectionToViewer([assetId], selectionPath, side);
      else sendClearToViewer(side);
    },
    [setSelectionPath, sendSelectionToViewer, sendClearToViewer]
  );

  return { selectionPaths, selectFromTree };
}
